import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { OrganizationLinksModal } from "@/components/organization-links/OrganizationLinksModal.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { TimeOffHistoryDetailsModal } from "@/pages/time-off/widgets/TimeOffHistoryModal.tsx";
import {
  useEmployeeDetails,
  useEmployeeMe,
  useEmployeeTimeOffCategoriesData,
} from "@/queries/employees/use-employees.query.ts";
import { useOrganizationLinks } from "@/queries/use-organization-links.query.ts";
import {
  ApiEmployeeTimeOffCategoryPayload,
  getEmployeeAvatarUrl,
} from "@/types/employees/employee.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  ApiTimeOffCategoryType,
  ApiTimeOffPeriodTrackingTime,
} from "@/types/time-off/time-off-policy.types.ts";
import { CardMini, FontAwesomeIcon, Title, toColorName, useModals } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const HomeMyCompany = () => {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const { openModal } = useModals();
  const { canEditField, isFeatureEnabled } = usePermissions();

  const { data: employeeFull } = useEmployeeDetails(employee?.id || undefined);

  const canRequestTimeOff =
    isFeatureEnabled(ApiFeatureType.time_off) &&
    canEditField(employeeFull, EmployeeFieldPermission.employee_time_off);
  const [payloadCategory] = useState<ApiEmployeeTimeOffCategoryPayload>({
    date_balance: new Date(),
  });
  const { data: employeeTimeOffCategories } = useEmployeeTimeOffCategoriesData(
    employeeFull?.id ?? 0,
    payloadCategory,
    canRequestTimeOff
  );

  const { data: organizationLinks } = useOrganizationLinks();

  if (!employee) return null;

  return (
    <>
      <Title
        header={t("core:my_company_template", { company: employee.organization.title })}
        size={"lg"}
        paddingBottom
        paddingTop
        actionIcon={<FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />}
        to={`/employees/${employee.id}`}
      />
      <div className="flex gap-(--gap-md) flex-wrap">
        <CardMini
          backgroundUrl={getEmployeeAvatarUrl(employee, "4xl")}
          title={t("common:you")}
          caption={t("core:my_profile")}
          to={`/employees/${employee.id}`}
        />
        {employeeTimeOffCategories?.map((timeOffCategory) => {
          const periodLocaleKey =
            timeOffCategory.period_track_type === ApiTimeOffPeriodTrackingTime.hours
              ? "plurals:plural_hours"
              : "plurals:plural_day";

          return (
            <CardMini
              key={timeOffCategory.id}
              title={timeOffCategory.title}
              accentColor={toColorName(timeOffCategory.color)}
              emoji={timeOffCategory.emoji}
              caption={
                timeOffCategory.category_type !== ApiTimeOffCategoryType.flexible
                  ? t(periodLocaleKey, {
                      count: parseFloat(timeOffCategory.balance.available_balance),
                    })
                  : t("time_off:available")
              }
              onClick={
                canRequestTimeOff &&
                timeOffCategory.category_type === ApiTimeOffCategoryType.accrues_time
                  ? () => {
                      openModal(TimeOffHistoryDetailsModal, {
                        employeeId: employee.id,
                        timeOffCategory: timeOffCategory,
                      });
                    }
                  : undefined
              }
              to={
                canRequestTimeOff &&
                timeOffCategory.category_type !== ApiTimeOffCategoryType.accrues_time
                  ? `/time-off/request/new?categoryId=${timeOffCategory.id}`
                  : undefined
              }
            />
          );
        })}
        {organizationLinks && organizationLinks.length > 0 && (
          <CardMini
            emoji={emojiDefaults.organizationLink.default}
            title={t("core:organization_links")}
            caption={t("plurals:links_count", { count: organizationLinks.length })}
            onClick={() => openModal(OrganizationLinksModal)}
          />
        )}
      </div>
    </>
  );
};
